import { Helmet, HelmetProvider } from 'react-helmet-async';

// Define a more flexible Schema type that can accommodate various schema structures
type SchemaObject = Record<string, any>;

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  type?: 'website' | 'article';
  schema?: SchemaObject | SchemaObject[] | null;
}

export function SEO({ 
  title = 'Home Buying Calculators | Plan Your Home Purchase',
  description = 'Free interactive home affordability calculator with visual timeline. Compare scenarios for home price, purchase date, and required savings. Features automatic data saving and detailed financial projections for smarter home buying decisions.',
  canonicalUrl = 'https://buyhomecalc.com',
  type = 'website',
  schema = null
}: SEOProps) {
  const fullTitle = title.includes('|') ? title : `${title} | Home Affordability Calculator`;
  
  // Create the basic website schema
  const websiteSchema: SchemaObject = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BuyHomeCalc",
    "url": "https://buyhomecalc.com",
    "description": "Interactive home buying calculators and tools",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://buyhomecalc.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };
  
  // Create the organization schema
  const organizationSchema: SchemaObject = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BuyHomeCalc",
    "url": "https://buyhomecalc.com",
    "logo": "https://buyhomecalc.com/logo.png"
  };
  
  // Combine default schemas with any custom schema passed in
  const schemas: SchemaObject[] = [websiteSchema, organizationSchema];
  
  // Add custom schema if provided
  if (schema) {
    if (Array.isArray(schema)) {
      schemas.push(...schema);
    } else {
      schemas.push(schema);
    }
  }
  
  return (
    <HelmetProvider>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />

        {/* OpenGraph */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={canonicalUrl} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemas)}
        </script>
      </Helmet>
    </HelmetProvider>
  );
}